import React, { useEffect, useState, forwardRef, useMemo } from 'react';
import { optionsWithSelection, SelectSearch } from '~/ui/components';
import { debounce } from 'lodash';
import { useAwaitControl } from 'react-redux-await-control';
import { getTicketResponsibles } from '~/store/tickets/actions';

export type ResponsibleSelectProps = {
  value?: number | string | null | undefined;
  onChange?: (value: string) => void;
  id?: string;
  label?: string;
  className?: string;
  width?: number;
};

export const WITHOUT_RESPONSIBLE_ID = 9999999999;

export const ResponsibleSelect = forwardRef<HTMLDivElement, ResponsibleSelectProps>(
  ({ value, onChange, className, width, ...props }, ref) => {
    const [currentValue, setCurrentValue] = useState({} as { value: string; label: string });
    const [searchString, setSearchString] = useState('');

    const getTicketResponsibleControl = useAwaitControl(getTicketResponsibles);
    const isLoadingResponsible = getTicketResponsibleControl.isRunning(props?.id || 'responsible');
    const responsibleList = getTicketResponsibleControl.result(props?.id || 'responsible');

    const responsibleOptions = useMemo(() => {
      const list = optionsWithSelection(responsibleList?._embedded?.responsibleList, currentValue) || [];
      return [{ label: 'Sem atribuição', value: WITHOUT_RESPONSIBLE_ID, initials: '-' }, ...list];
    }, [responsibleList]);

    const handleOnChange = (option: { value: string; label: string }) => {
      setCurrentValue(option);

      if (onChange) {
        onChange(option?.value);
      }
    };

    const handleSearch = debounce((searchedValue) => {
      setSearchString(searchedValue);

      getTicketResponsibleControl.start(
        {
          page: 0,
          size: 50,
          sort: 'string',
          responsibleName: searchedValue,
        },
        { actionId: props?.id || 'responsible' },
      );
    }, 500);

    useEffect(() => {
      if (!responsibleList) {
        getTicketResponsibleControl.start(
          { page: 0, size: 20, sort: 'string' },
          { actionId: props?.id || 'responsible' },
        );
      }
    }, []);

    return (
      <SelectSearch
        ref={ref}
        placeholder={props.label || 'Responsável'}
        pictured
        searchValue={searchString}
        value={value ? { value: Number(value) } : null}
        options={responsibleOptions}
        onChange={handleOnChange}
        onSearch={handleSearch}
        loading={isLoadingResponsible}
        width={width}
        className={className}
      />
    );
  },
);
